import { TipStatus } from "model/Tip";
import type { TipDetails, Worker } from "model/Tip";
import { parseWorkTime } from "utils/utils";
import { getWorkerName } from "../logic/tips.worker.logic";
import { formatHours, formatMonthTitle, formatReportDate } from "../logic/tips.format.logic";

export type MonthlyReportDetailStatus =
  | "bank"
  | "distributed"
  | "none"
  | "swish";

export interface MonthlyReportDetail {
  readonly amount: number;
  readonly date: string;
  readonly hours: number;
  readonly id: string;
  readonly status: MonthlyReportDetailStatus;
  readonly unit: string | null;
}

export interface MonthlyReportRow {
  readonly details: readonly MonthlyReportDetail[];
  readonly distributedAmount: number;
  readonly externalId: string | null;
  readonly name: string;
  readonly paidOutAmount: number;
  readonly role: string | null;
  readonly totalHours: number;
  readonly totalTipsAmount: number;
  readonly workerId: number;
}

export interface MonthlyReportTotals {
  readonly distributedAmount: number;
  readonly paidOutAmount: number;
  readonly totalHours: number;
  readonly totalTipsAmount: number;
}

interface GetStatusLabelParams {
  readonly status: MonthlyReportDetailStatus;
  readonly t: (key: string) => string;
}

interface GetMonthlyReportDetailStatusParams {
  readonly tip: TipDetails;
  readonly worker: Worker;
}

interface AppendWorkerTipParams {
  readonly row: MonthlyReportRow;
  readonly tip: TipDetails;
  readonly worker: Worker;
}

function workTimeToHours(workTime: string): number {
  const parsedTime = parseWorkTime(workTime);
  return parsedTime.hours + parsedTime.minutes / 60;
}

function isPaidOutTip(tip: TipDetails): boolean {
  return (
    tip.status === TipStatus.Closed || tip.status === TipStatus.ClosedManually
  );
}

export function getMonthlyReportDetailStatus({
  tip,
  worker,
}: GetMonthlyReportDetailStatusParams): MonthlyReportDetailStatus {
  if (tip.status === TipStatus.Closed) return "swish";
  if (tip.status === TipStatus.ClosedManually) return "bank";
  if (worker.tipAmount > 0) return "distributed";
  return "none";
}

export function getStatusLabel({ status, t }: GetStatusLabelParams): string {
  if (status === "swish") return t("Tips.Swish");
  if (status === "bank") return t("MonthlyTipReport.Bank");
  if (status === "distributed") return t("MonthlyTipReport.Distributed");
  return t("Tips.NotInitiated");
}

export function getStatusVariant(
  status: MonthlyReportDetailStatus,
): "bank" | "neutral" | "swish" {
  if (status === "bank") return "bank";
  if (status === "swish") return "swish";
  return "neutral";
}

function createMonthlyReportRow(worker: Worker): MonthlyReportRow {
  return {
    details: [],
    distributedAmount: 0,
    externalId: worker.externalId ?? null,
    name: getWorkerName(worker),
    paidOutAmount: 0,
    role: worker.workGroupName ?? null,
    totalHours: 0,
    totalTipsAmount: 0,
    workerId: worker.workerId,
  };
}

function appendWorkerTip({
  row,
  tip,
  worker,
}: AppendWorkerTipParams): MonthlyReportRow {
  const hours = workTimeToHours(worker.workTime);
  const isPaidOut = isPaidOutTip(tip);

  return {
    ...row,
    details: [
      ...row.details,
      {
        amount: worker.tipAmount,
        date: tip.date,
        hours,
        id: `${tip.companyTipId}-${worker.workerId}`,
        status: getMonthlyReportDetailStatus({ tip, worker }),
        unit: worker.workGroupName ?? null,
      },
    ],
    distributedAmount:
      row.distributedAmount + (isPaidOut ? 0 : worker.tipAmount),
    externalId: row.externalId ?? worker.externalId ?? null,
    paidOutAmount: row.paidOutAmount + (isPaidOut ? worker.tipAmount : 0),
    role: row.role ?? worker.workGroupName ?? null,
    totalHours: row.totalHours + hours,
    totalTipsAmount: row.totalTipsAmount + worker.tipAmount,
  };
}

export function buildMonthlyReports(
  tips: readonly TipDetails[],
): readonly MonthlyReportRow[] {
  const rowsByWorkerId = new Map<number, MonthlyReportRow>();

  tips.forEach((tip) => {
    tip.workers.forEach((worker) => {
      const currentRow =
        rowsByWorkerId.get(worker.workerId) ?? createMonthlyReportRow(worker);
      rowsByWorkerId.set(
        worker.workerId,
        appendWorkerTip({ row: currentRow, tip, worker }),
      );
    });
  });

  return Array.from(rowsByWorkerId.values()).sort((firstRow, secondRow) =>
    firstRow.name.localeCompare(secondRow.name),
  );
}

export function buildMonthlyReportTotals(
  rows: readonly MonthlyReportRow[],
): MonthlyReportTotals {
  return rows.reduce<MonthlyReportTotals>(
    (totals, row) => ({
      distributedAmount: totals.distributedAmount + row.distributedAmount,
      paidOutAmount: totals.paidOutAmount + row.paidOutAmount,
      totalHours: totals.totalHours + row.totalHours,
      totalTipsAmount: totals.totalTipsAmount + row.totalTipsAmount,
    }),
    {
      distributedAmount: 0,
      paidOutAmount: 0,
      totalHours: 0,
      totalTipsAmount: 0,
    },
  );
}
